import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { importFilesSSE, fetchFolderTree, FolderTreeItem, fetchTags, assignTag } from '../../store/actions/FolderAction';
import { fetchFolderContents, fetchRootFiles } from '../../utils/FileApiBuilder';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { FolderProps, TagItem } from './documents/types';
import Badge from '../shared/TailwindComponents/Badge';
import FieldShell, { FOCUS_RING } from '../shared/TailwindComponents/FieldShell';
import RadioGroup from '../shared/TailwindComponents/RadioGroup';
import { Well } from '../shared/brand/Field';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { Collaborator } from '../shared/CollaboratorSelector';
import Button from '../shared/TailwindComponents/Button';
import { FormField, FormSchema, FormValidator } from '../../utils/validation/form-schema';
import { sanitizeString } from '../../utils/validation';
import { FILE_IMPORT_RESET } from '../../store/types';
import { PiCheckCircleLight, PiXCircleLight, PiSpinnerLight, PiWarningCircleLight, PiFileLight, PiArrowLeftLight } from 'react-icons/pi';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, importFilesFormTourSteps } from '../shared/FeatureTour';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import Spinner from '../shared/TailwindComponents/Spinner';

interface CreateFolderWithFilesFormProps {
  onClose: () => void;
  existingFolders: FolderProps[];
  selectedFolderItem?: any;
  onRefresh?: (createdFolderId?: string) => void | Promise<void>;
}

const CreateFolderWithFilesForm: React.FC<CreateFolderWithFilesFormProps> = ({
  onClose,
  existingFolders,
  selectedFolderItem,
  onRefresh
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => (state as any).auth?.user);

  const { isConnecting, connected, steps, ok, summary, complete } = useSelector(
    (state: RootState) => state.folders
  );

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [selectedParentFolderID, setSelectedParentFolderID] = useState<string>(selectedFolderItem?.FolderID || "");
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toggle, setToggle] = useState<'existing' | 'new'>('existing');
  const [result, setResult] = useState<any>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [recievedFolderID, setRecievedFolderID] = useState<string | null>(null);
  const [folderTree, setFolderTree] = useState<FolderTreeItem[]>([]);
  const [adminUuid, setAdminUuid] = useState<string | undefined>(undefined);
  const [orgTags, setOrgTags] = useState<TagItem[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  // What was submitted, so the success handler can find the files it created:
  // the destination folder chosen in the form, and the names that went up.
  const destinationFolderIdRef = useRef<string>('');
  const importedFileNamesRef = useRef<string[]>([]);
  const finalizedRef = useRef(false);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'import-files-form',
    steps: importFilesFormTourSteps,
    autoStartDelay: 600,
  });

  // Reset SSE state on mount to clear stale data from previous imports
  useEffect(() => {
    dispatch({ type: FILE_IMPORT_RESET });
  }, [dispatch]);

  // Fetch folder tree and tags on mount
  useEffect(() => {
    setIsLoadingData(true);
    Promise.all([
      (dispatch(fetchFolderTree()) as any).catch(() => []),
      (dispatch(fetchTags()) as any).catch(() => []),
    ]).then(([tree, tags]) => {
      setFolderTree(tree || []);
      setOrgTags(tags || []);
      setIsLoadingData(false);
    });
  }, [dispatch]);

  // Build hierarchical dropdown options from folder tree
  const folderOptions: DropdownOption[] = useMemo(() => {
    const options = folderTree.map((f) => ({
      id: f.id,
      value: f.id,
      label: f.label,
      parent_id: f.parent_id,
    }));

    // Add the currently viewed folder if provided and not already in tree
    if (selectedFolderItem && !options.find(o => o.id === selectedFolderItem.FolderID)) {
      options.push({
        id: selectedFolderItem.FolderID,
        value: selectedFolderItem.FolderID,
        label: selectedFolderItem.FolderName,
        parent_id: null,
      });
    }

    return options;
  }, [folderTree, selectedFolderItem]);

  useEffect(() => {
    setResult(summary);
    if (summary && summary.folderId) {
      setRecievedFolderID(summary.folderId);
    }
  }, [summary]);

  // Set collaborators from tree data when parent folder changes
  useEffect(() => {
    if (selectedParentFolderID) {
      const selectedFolder = folderTree.find(f => f.id === selectedParentFolderID);
      if (selectedFolder && selectedFolder.collaborators) {
        // Find admin collaborator
        const admin = selectedFolder.collaborators.find((c: any) => c.access === 'admin');
        setAdminUuid(admin?.user_uuid);

        // Filter out current user, map to collaborator format
        const filtered = selectedFolder.collaborators
          .filter((c: any) => c.user_uuid !== currentUser?.uuid)
          .map((c: any) => ({
            collaborator_uuid: c.user_uuid,
            email: c.email,
            name: c.name,
            access: c.access || 'view',
            role: c.role,
            initials: c.initials,
            avatar_url: c.avatar_url
          }));
        setCollaborators(filtered);
      } else {
        setCollaborators([]);
        setAdminUuid(undefined);
      }
    } else {
      setCollaborators([]);
      setAdminUuid(undefined);
    }
  }, [selectedParentFolderID, folderTree, currentUser?.uuid]);

  // Clear collaborators when toggle switches to 'new'
  useEffect(() => {
    if (toggle === 'new') {
      setCollaborators([]);
      setSelectedParentFolderID('');
      setAdminUuid(undefined);
    }
  }, [toggle]);

  /**
   * Land on the destination folder in ONE transition.
   *
   * This used to wait 300ms, dispatch the reset and only then push — and
   * because the form is rendered from a query param on the Files page, tearing
   * it down first painted the Files root before the push landed, which is the
   * flash of "Files, then the folder". Push first; the destination owns its
   * own loading.
   */
  const goToDestination = useCallback(async (folderId: string) => {
    const targetPath = folderId ? `/files/${folderId}` : '/files';
    const currentPath = location.pathname.replace(/\/$/, '');

    dispatch({ type: FILE_IMPORT_RESET });

    if (currentPath === targetPath) {
      // Already on the destination: nothing to navigate to, just refresh it.
      if (onRefresh) await onRefresh(folderId || undefined);
      onClose();
      return;
    }

    history.push(targetPath);
  }, [dispatch, history, location.pathname, onClose, onRefresh]);

  const handleClose = async () => {
    await goToDestination(recievedFolderID || destinationFolderIdRef.current || '');
  };

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    return [
      {
        title: t('Destination'),
        description: t('Choose where the imported files should land.'),
        fields: [
          {
            name: 'folderMode',
            type: 'custom',
            label: t('Folder'),
            customComponent: ({ value, onChange, error }) => (
              <RadioGroup
                label={t('Folder')}
                name="folderMode"
                orientation="horizontal"
                value={toggle}
                error={error}
                options={[
                  { value: 'existing', label: 'Use existing' },
                  { value: 'new', label: 'Create new' },
                ]}
                onChange={(next) => {
                  setToggle(next as 'existing' | 'new');
                  onChange(next);
                }}
              />
            ),
            gridSize: 'full'
          } as BaseFormFieldConfig,

          {
            name: 'parentFolder',
            type: 'dropdown',
            label: toggle === 'new' ? t('Parent Folder') : t('Path'),
            options: folderOptions,
            placeholder: t('Choose a folder'),
            // Optional by omission (no required marker); the description says
            // what leaving it empty does rather than tagging it "(optional)".
            // BaseForm's dropdown branch forwards customProps, not `description`.
            required: false,
            customProps: {
              fullWidth: true,
              hierarchical: true,
              description:
                toggle === 'new'
                  ? t('Leave empty to create the folder at the root level.')
                  : undefined,
            },
            gridSize: 'full',
            onChange: (value: string) => {
              if (toggle === 'existing') {
                setSelectedParentFolderID(value);
              }
            }
          } as BaseFormFieldConfig,

          ...(toggle === 'new' ? [{
            name: 'folderName',
            type: 'input',
            label: t('Folder Name'),
            placeholder: t('e.g., Contracts 2025'),
            required: true,
            gridSize: 'full'
          }] : []) as BaseFormFieldConfig[]
        ]
      },
      ...(orgTags.length > 0 ? [{
        title: t('Tags'),
        description: t('Optional labels that make this folder easier to find later.'),
        fields: [
          {
            name: 'tags',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: ({ value, onChange, error }: any) => {
              const selected: number[] = value || [];
              return (
                <FieldShell error={error} translate={false}>
                  <div className="flex flex-wrap gap-1.5">
                    {orgTags.map(tag => {
                      const isSelected = selected.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => {
                            const next = isSelected
                              ? selected.filter((id: number) => id !== tag.id)
                              : [...selected, tag.id];
                            onChange(next);
                            setSelectedTagIds(next);
                          }}
                          className={`rounded-full transition-all ${FOCUS_RING}`}
                        >
                          {/* Selected state lives on the badge — a solid
                              coloured border and a check — not as a soft ring
                              floating around the pill. */}
                          <Badge
                            color={(tag.color as BadgeColor) || 'gray'}
                            selected={isSelected}
                            className="text-xs cursor-pointer"
                          >
                            {tag.name}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </FieldShell>
              );
            }
          } as BaseFormFieldConfig
        ]
      }] : []),
      {
        title: t('Share with team members'),
        description: toggle === 'existing' && selectedParentFolderID
          ? t('People from parent folder are pre-selected. Admin access cannot be removed.')
          : t('Add people who can access this folder'),
        fields: [
          {
            // The section title is the label — the control draws no second one.
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: '',
            gridSize: 'full',
            customProps: { adminUuid }
          } as BaseFormFieldConfig
        ]
      },
      {
        title: t('Files to import'),
        description: t('Drop files here or browse to select them.'),
        fields: [
          {
            name: 'files',
            type: 'fileUploader',
            label: t('Files'),
            gridSize: 'full',
            required: true,
          } as BaseFormFieldConfig
        ]
      }
    ];
  }, [toggle, folderOptions, t, selectedParentFolderID, adminUuid, orgTags]);

  const schema = useMemo(() => {
    const schemaFields: FormField[] = [
      new FormField('folderMode', 'text', false, 'Folder Mode', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('parentFolder', 'text', false, 'Parent Folder', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('folderName', 'text', toggle === 'new', t('Folder Name'), {
        customValidator: (value: any) => {
          if (toggle === 'new' && (!value || !String(value).trim())) {
            return { isValid: false, error: t('Folder name is required') };
          }
          return { isValid: true, sanitized: value };
        },
      }),
      new FormField('tags', 'text', false, 'Tags', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('collaborators', 'text', false, 'Access', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('files', 'file', true, t('Files'), {
        customValidator: (value: any) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { isValid: false, error: t('Please add at least one file') };
          }
          return { isValid: true, sanitized: value };
        },
      }),
    ];

    return new FormSchema('CreateFolderWithFiles', schemaFields);
  }, [toggle, t]);

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      setIsSubmitting(true);
      finalizedRef.current = false;
      destinationFolderIdRef.current = toggle === 'existing' ? (formData.parentFolder || '') : '';
      importedFileNamesRef.current = (formData.files || []).map((f: File) => f?.name).filter(Boolean);

      const sanitizedFolderName = toggle === 'new' && formData.folderName
        ? sanitizeString(formData.folderName, 255, false)
        : '';

      const validCollaborators = (formData.collaborators || [])
        .map((c: any) => ({
          user_uuid: c.collaborator_uuid || c.user_id,
          access: c.access
        }))
        .filter((c: any) => c.user_uuid);

      await dispatch(
        importFilesSSE(
          {
            FolderID: toggle === 'existing' ? formData.parentFolder : undefined,
            FolderName: sanitizedFolderName,
            collaborators: validCollaborators.length > 0 ? validCollaborators : undefined
          },
          formData.files || []
        )
      );

      // Note: isSubmitting will be set to false in the SSE Redux action when upload completes
    } catch (error: any) {
      console.error('[CreateFolderWithFilesForm] Submission error:', error);
      setIsSubmitting(false);
    }
  };

  /**
   * The tags chosen during an import belong to THE FILES BEING IMPORTED.
   *
   * They used to go to `assignTag(tagId, 'add', undefined, summary.folderId)`
   * — a literal `undefined` file id and the folder id filled in — so every tag
   * landed on the folder and none on the documents, and importing into an
   * existing folder (no new folder id in the summary) dropped them entirely.
   * The file ids come from the import summary when the backend sends them, and
   * otherwise from reading the destination folder back and matching the names
   * that were uploaded.
   */
  const normaliseName = (name: string): string =>
    String(name || '').split('/').pop()!.trim().toLowerCase();

  const resolveImportedFileIds = useCallback(async (folderId: string): Promise<string[]> => {
    // Preferred: ids straight off the summary, whichever shape it uses.
    const fromSummary: string[] = [
      ...(Array.isArray(summary?.fileIds) ? summary.fileIds : []),
      ...(Array.isArray(summary?.files) ? summary.files.map((f: any) => f?.FileID || f?.file_id || f?.id) : []),
    ].filter(Boolean);
    if (fromSummary.length > 0) return fromSummary;

    // Fallback: read the destination back and match on the uploaded names.
    try {
      const res = folderId
        ? await fetchFolderContents(folderId, 1, undefined, { sortBy: 'created_at', sortOrder: 'desc' })
        : await fetchRootFiles({ page: 1, sortBy: 'created_at', sortOrder: 'desc' });
      const files = (res?.files && res.files.length > 0)
        ? res.files
        : (res?.items || []).filter((i: any) => i?.FileID);
      const wanted = new Set(importedFileNamesRef.current.map(normaliseName));
      return files
        .filter((f: any) => wanted.has(normaliseName(f.FileName)))
        .map((f: any) => f.FileID)
        .filter(Boolean);
    } catch (err) {
      console.error('[CreateFolderWithFilesForm] Could not resolve imported file ids:', err);
      return [];
    }
  }, [summary]);

  // Apply the tags, then land on the destination folder — one transition, no
  // "Go to Folder" click required.
  useEffect(() => {
    if (!complete || finalizedRef.current) return;

    if (!ok) {
      // Failure keeps the result panel up so the messages can be read.
      setIsSubmitting(false);
      return;
    }

    finalizedRef.current = true;
    (async () => {
      const destinationId = summary?.folderId || destinationFolderIdRef.current || '';

      if (selectedTagIds.length > 0) {
        const fileIds = await resolveImportedFileIds(destinationId);
        if (fileIds.length > 0) {
          for (const fileId of fileIds) {
            for (const tagId of selectedTagIds) {
              // eslint-disable-next-line no-await-in-loop -- keep the writes ordered and cheap
              await dispatch(assignTag(tagId, 'add', fileId, undefined) as any);
            }
          }
        } else {
          dispatch(showFeedbackModal({
            showModal: true,
            modalType: 'warning',
            message: t('Files imported, but the tags could not be applied.'),
            duration: 5000,
          }));
        }
      }

      setIsSubmitting(false);
      await goToDestination(destinationId);
    })();
  }, [complete, ok, summary, selectedTagIds, dispatch, resolveImportedFileIds, goToDestination, t]);

  // Nothing survives this form: the import slice is reset on the way out as
  // well as on the way in, so the next open cannot inherit a previous run's
  // progress, result or destination.
  useEffect(() => {
    return () => {
      dispatch({ type: FILE_IMPORT_RESET });
    };
  }, [dispatch]);

  // Derive upload state for inline display
  const isUploading = isSubmitting && (isConnecting || connected || steps?.length > 0);
  const uploadComplete = complete && !isSubmitting;
  const completedCount = steps?.filter((s: any) => s.status === 'Completed').length || 0;
  const failedCount = steps?.filter((s: any) => s.status === 'Failed').length || 0;
  const totalCount = steps?.length || 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Upload progress inline component
  const renderUploadProgress = () => {
    if (!isUploading && !uploadComplete) return null;

    return (
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          {isUploading ? (
            <Spinner size="glyph" className="!h-5 !w-5 flex-shrink-0" />
          ) : result?.ok ? (
            <PiCheckCircleLight className="w-5 h-5 text-success-600 flex-shrink-0" />
          ) : (
            <PiWarningCircleLight className="w-5 h-5 text-danger-500 flex-shrink-0" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium text-fg-strong">
              {isUploading
                ? isConnecting
                  ? t('Connecting...')
                  : `${t('Uploading')} ${completedCount}/${totalCount} ${t('files')}`
                : result?.ok
                  ? failedCount > 0
                    ? t('Import completed with errors')
                    : t('Files imported successfully')
                  : t('Import failed')}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="w-full bg-surface-sunken border border-hairline-soft rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full ${
                uploadComplete && !result?.ok ? 'bg-danger-500' : 'bg-champagne'
              }`}
              style={{
                width: `${uploadComplete ? 100 : progressPercent}%`,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>
        )}

        {/* File list */}
        {steps && steps.length > 0 && (
          <div className="flex-1 min-h-0 max-h-80 space-y-1.5 overflow-y-auto">
            {steps.map((step: any, idx: number) => (
              <Well
                key={idx}
                className="flex items-center gap-2.5 !py-1.5 !px-2 text-sm"
              >
                <div className="flex-shrink-0">
                  {step.status === 'Completed' ? (
                    <PiCheckCircleLight className="w-4 h-4 text-success-600" />
                  ) : step.status === 'Failed' ? (
                    <PiXCircleLight className="w-4 h-4 text-danger-500" />
                  ) : step.status === 'In Progress' ? (
                    <Spinner size="glyph" className="!h-4 !w-4" />
                  ) : (
                    <PiFileLight className="w-4 h-4 text-fg-muted" />
                  )}
                </div>
                <span className={`flex-1 truncate ${
                  step.status === 'Failed' ? 'text-danger-600 dark:text-danger-400' : 'text-fg-body'
                }`}>
                  {step.label || `File ${idx + 1}`}
                </span>
                {step.status === 'Failed' && step.messages?.length > 0 && (
                  <span className="text-xs text-danger-500 flex-shrink-0">
                    {step.messages[step.messages.length - 1]}
                  </span>
                )}
              </Well>
            ))}
          </div>
        )}

        {/* Result summary */}
        {uploadComplete && result && (
          <div className={`flex items-center justify-between px-3 py-2 rounded-md text-xs ${
            result.ok
              ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300'
              : 'bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300'
          }`}>
            <span>{result.message}</span>
          </div>
        )}

        {/* Action buttons */}
        {uploadComplete && (
          <div className="flex justify-end gap-2 pt-1 mt-auto">
            {!result?.ok && (
              <Button
                type="button"
                variant="neutral"
                size="small"
                onClick={() => {
                  dispatch({ type: FILE_IMPORT_RESET });
                  setResult(null);
                }}
              >
                <PiArrowLeftLight className="w-3.5 h-3.5 mr-1" />
                {t('Try Again')}
              </Button>
            )}
            <Button
              type="button"
              variant={result?.ok ? 'primary' : 'neutral'}
              size="small"
              onClick={handleClose}
            >
              {result?.ok ? t('Go to Folder') : t('Close')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Build form sections — inject upload progress as a custom field when uploading
  const activeSections: BaseFormSectionConfig[] = useMemo(() => {
    if (isUploading || uploadComplete) {
      return [{
        fields: [{
          name: '_uploadProgress',
          type: 'custom',
          label: '',
          customComponent: () => renderUploadProgress(),
          gridSize: 'full'
        } as BaseFormFieldConfig]
      }];
    }
    return formSections;
  }, [isUploading, uploadComplete, formSections, steps, result, isConnecting, connected, completedCount, totalCount]);

  if (isLoadingData) {
    return <Skeleton variant="form" rows={6} />;
  }

  return (
    <div className="flex flex-1 flex-col min-h-0" data-tour-id="import-files-form">
      <BaseForm
        title={isUploading ? t('Importing Files') : uploadComplete ? (result?.ok ? t('Import Complete') : t('Import Failed')) : t('Create Folder & Import Files')}
        description={isUploading ? t('Please wait while your files are being uploaded.') : uploadComplete ? '' : t('Set up a new folder and upload files, or upload to an existing folder.')}
        sections={activeSections}
        schema={schema}
        initialValues={{
          folderMode: 'existing',
          parentFolder: (selectedFolderItem?.FolderID) || '',
          folderName: '',
          tags: [],
          collaborators: collaborators,
          files: []
        }}
        onSubmit={handleFormSubmit}
        onCancel={isUploading || uploadComplete ? undefined : onClose}
        submitLabel={t('Import Files')}
        cancelLabel={t('Cancel')}
        isSubmitting={isSubmitting}
        hideActions={isUploading || uploadComplete}
        hideLoadingOverlay={isUploading || uploadComplete}
        maxWidth="max-w-3xl"
        loadingText={t('Importing files...')}
      />
      <FeatureTour
        steps={importFilesFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default CreateFolderWithFilesForm;
